if (!customElements.get('rtd-student-information')) {
  customElements.define(
    'rtd-student-information',
    class RtdStudentInformation extends HTMLElement {
      constructor() {
        super();

        this.requiredFields = [...this.querySelectorAll('[data-rtd-student-information-required]')];
        this.fields = [...this.querySelectorAll('input[name^="attributes["]')];
        this.emailField = this.querySelector('input[type="email"]');
        this.statusElement = this.querySelector('[data-rtd-student-information-status]');
        this.schoolField = this.querySelector('[data-school-input]');
        this.schoolUnlisted = this.querySelector('[data-school-unlisted]');
        this.schoolCustom = this.querySelector('[data-school-custom]');
        this.schoolSuggestions = this.querySelector('[data-school-suggestions]');
        this.schoolHint = this.querySelector('[data-school-hint]');
        this.schools = JSON.parse(this.querySelector('[data-school-list]').textContent);
        this.schoolNames = new Map(this.schools.map((name) => [this.normalizeSchool(name), name]));
        this.activeSchoolIndex = -1;
        this.onSchoolKeydownHandler = this.onSchoolKeydown.bind(this);
        this.onSchoolFocusHandler = () => this.showSchoolSuggestions();
        this.onSchoolBlurHandler = (event) => {
          if (!this.schoolSuggestions.contains(event.relatedTarget)) this.closeSchoolSuggestions();
        };
        this.isSaving = false;
        this.changeVersion = 0;
        this.lastSavedVersion = 0;
        this.hasUnsavedChanges = false;
        this.onInputHandler = this.onInput.bind(this);
        this.onChangeHandler = this.onChange.bind(this);
        this.onStudentInformationSavedHandler = this.onStudentInformationSaved.bind(this);
        this.debouncedSave = debounce(this.save.bind(this), 500);
      }

      connectedCallback() {
        this.addEventListener('input', this.onInputHandler);
        this.addEventListener('change', this.onChangeHandler);
        this.schoolField.addEventListener('keydown', this.onSchoolKeydownHandler);
        this.schoolField.addEventListener('focus', this.onSchoolFocusHandler);
        this.schoolField.addEventListener('blur', this.onSchoolBlurHandler);
        document.addEventListener('rtd:student-information-saved', this.onStudentInformationSavedHandler);
        this.closest('cart-notification')?.querySelector('.cart-notification')?.classList.add('cart-notification--student-information');
        this.validateEmailField(this.emailField);
        this.validateSchool();
        this.updateCheckoutAvailability();
      }

      disconnectedCallback() {
        this.removeEventListener('input', this.onInputHandler);
        this.removeEventListener('change', this.onChangeHandler);
        this.schoolField.removeEventListener('keydown', this.onSchoolKeydownHandler);
        this.schoolField.removeEventListener('focus', this.onSchoolFocusHandler);
        this.schoolField.removeEventListener('blur', this.onSchoolBlurHandler);
        document.removeEventListener('rtd:student-information-saved', this.onStudentInformationSavedHandler);
      }

      onInput(event) {
        if (event.target === this.schoolUnlisted) return;
        event.stopPropagation();
        this.validateEmailField(event.target);
        if (event.target === this.schoolField) {
          this.validateSchool();
          this.showSchoolSuggestions();
        }
        this.markChanged();
        this.updateCheckoutAvailability();
        this.debouncedSave();
      }

      onChange(event) {
        event.stopPropagation();
        if (event.target === this.schoolUnlisted) {
          this.closeSchoolSuggestions();
          this.validateSchool();
          this.schoolField.focus();
        }
        this.validateEmailField(event.target);
        if (event.target === this.schoolField) this.validateSchool();
        this.markChanged();
        this.updateCheckoutAvailability();
        this.debouncedSave();
      }

      markChanged() {
        this.changeVersion += 1;
        this.hasUnsavedChanges = true;
      }

      normalizeSchool(value) {
        return value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
      }

      validateSchool() {
        const value = this.schoolField.value.trim();
        const match = this.schoolNames.get(this.normalizeSchool(value));
        const invalid = !!value && !this.schoolUnlisted.checked && !match;
        this.schoolField.setCustomValidity(invalid ? this.schoolHint.dataset.invalid : '');
        this.schoolField.toggleAttribute('aria-invalid', invalid);
        this.schoolField.setAttribute('aria-autocomplete', this.schoolUnlisted.checked ? 'none' : 'list');
        this.schoolCustom.value = this.schoolUnlisted.checked ? value : '';
        this.schoolHint.textContent = this.schoolUnlisted.checked ? this.schoolHint.dataset.custom : this.schoolHint.dataset.search;
      }

      closeSchoolSuggestions() {
        this.schoolSuggestions.hidden = true;
        this.schoolField.setAttribute('aria-expanded', 'false');
        this.schoolField.removeAttribute('aria-activedescendant');
        this.activeSchoolIndex = -1;
      }

      showSchoolSuggestions() {
        this.closeSchoolSuggestions();
        this.schoolSuggestions.replaceChildren();
        const query = this.normalizeSchool(this.schoolField.value);
        if (!query || this.schoolUnlisted.checked) return;
        const matches = this.schools.filter((name) => this.normalizeSchool(name).includes(query)).slice(0, 12);
        this.schoolHint.textContent = matches.length ? this.schoolHint.dataset.search : this.schoolHint.dataset.empty;
        matches.forEach((name, index) => {
          const option = document.createElement('li');
          option.id = `${this.schoolField.id}-option-${index}`;
          option.setAttribute('role', 'option');
          option.setAttribute('aria-selected', 'false');
          option.textContent = name;
          option.addEventListener('pointerdown', (event) => event.preventDefault());
          option.addEventListener('click', () => this.selectSchool(name));
          this.schoolSuggestions.appendChild(option);
        });
        this.schoolSuggestions.hidden = !matches.length;
        this.schoolField.setAttribute('aria-expanded', String(!!matches.length));
      }

      selectSchool(name) {
        this.schoolField.value = name;
        this.closeSchoolSuggestions();
        this.schoolField.dispatchEvent(new Event('change', { bubbles: true }));
      }

      onSchoolKeydown(event) {
        if (event.key === 'Escape') {
          event.stopPropagation();
          this.closeSchoolSuggestions();
          return;
        }
        if (event.key === 'Enter' && !this.schoolSuggestions.hidden) {
          event.preventDefault();
          if (this.activeSchoolIndex >= 0) this.selectSchool(this.schoolSuggestions.children[this.activeSchoolIndex].textContent);
          return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key) || this.schoolUnlisted.checked) return;
        event.preventDefault();
        if (this.schoolSuggestions.hidden) this.showSchoolSuggestions();
        const options = [...this.schoolSuggestions.children];
        if (!options.length) return;
        this.activeSchoolIndex = this.activeSchoolIndex < 0
          ? (event.key === 'ArrowDown' ? 0 : options.length - 1)
          : (this.activeSchoolIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeSchoolIndex)));
        const active = options[this.activeSchoolIndex];
        this.schoolField.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      }

      validateEmailField(field) {
        if (field !== this.emailField) return;

        const value = field.value.trim();
        const isInvalid = value !== '' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

        field.classList.toggle('rtd-student-information__input--invalid', isInvalid);
        field.toggleAttribute('aria-invalid', isInvalid);
        field.setCustomValidity(isInvalid ? 'Enter a valid email address.' : '');
      }

      isCompleteAndValid() {
        return this.requiredFields.every((field) => field.value.trim() && field.validity.valid);
      }

      updateCheckoutAvailability() {
        const canCheckout = !window.RtdFlexPreferences?.saving && [...document.querySelectorAll('rtd-student-information')].every(
          (form) => typeof form.isCompleteAndValid === 'function' && form.isCompleteAndValid() && !form.hasUnsavedChanges
        );

        document.querySelectorAll('[data-rtd-student-information-checkout]').forEach((checkoutButton) => {
          checkoutButton.disabled = !canCheckout;

          const message = document.getElementById(checkoutButton.dataset.rtdStudentInformationCheckoutMessageId);
          if (!message) return;

          message.hidden = canCheckout;
          if (canCheckout) {
            checkoutButton.removeAttribute('aria-describedby');
          } else {
            checkoutButton.setAttribute('aria-describedby', message.id);
          }
        });

        document.querySelectorAll('#main-cart-footer .additional-checkout-buttons').forEach((checkoutButtons) => {
          checkoutButtons.hidden = !canCheckout;
        });
      }

      onStudentInformationSaved(event) {
        const attributes = event.detail?.attributes;
        if (!attributes) return;
        if (this.hasUnsavedChanges && event.detail.source !== this) return;

        this.fields.forEach((field) => {
          const value = attributes[this.attributeKey(field)];
          if (typeof value !== 'string') return;
          if (field.type === 'checkbox') field.checked = value === 'Yes';
          else field.value = value;
        });

        this.hasUnsavedChanges = false;
        this.lastSavedVersion = this.changeVersion;
        this.validateEmailField(this.emailField);
        this.validateSchool();
        this.closeSchoolSuggestions();
        this.updateCheckoutAvailability();
      }

      save() {
        if (
          !this.isConnected ||
          !this.isCompleteAndValid() ||
          this.isSaving ||
          this.changeVersion === this.lastSavedVersion
        )
          return;

        const requestVersion = this.changeVersion;
        this.isSaving = true;
        this.setStatus('Saving student information...');

        const attributes = Object.fromEntries(this.fields.map((field) => [
          this.attributeKey(field), field.type === 'checkbox' ? (field.checked ? 'Yes' : '') : field.value.trim(),
        ]));
        if (!this.schoolUnlisted.checked) {
          attributes['Student high school'] = this.schoolNames.get(this.normalizeSchool(this.schoolField.value));
        }
        const body = JSON.stringify({ attributes });
        let attributesSaved = false;

        fetch(routes.cart_update_url, { ...fetchConfig(), body })
          .then((response) => {
            if (!response.ok) throw new Error('Cart update failed');
            return response.json();
          })
          .then((cart) => {
            if (!cart || cart.errors) throw new Error('Cart update failed');
            if (requestVersion !== this.changeVersion) return false;

            attributesSaved = true;
            this.lastSavedVersion = requestVersion;
            this.hasUnsavedChanges = false;
            document.dispatchEvent(new CustomEvent('rtd:student-information-saved', { detail: { attributes, source: this } }));
          })
          .then(() => {
            if (!attributesSaved || requestVersion !== this.changeVersion) return;

            this.updateCheckoutAvailability();
            this.setStatus('Student information saved.');
          })
          .catch(() => {
            if (requestVersion !== this.changeVersion) return;

            if (attributesSaved) {
              this.updateCheckoutAvailability();
              this.setStatus('Student information was saved, but checkout options could not be refreshed.');
            } else {
              this.setStatus('Student information could not be saved. Check your connection and try again.');
            }
          })
          .finally(() => {
            this.isSaving = false;

            if (requestVersion !== this.changeVersion) this.save();
          });
      }

      attributeKey(field) {
        return field.name.slice('attributes['.length, -1);
      }

      setStatus(message) {
        if (!this.statusElement) return;
        this.statusElement.hidden = !message;
        this.statusElement.textContent = message;
      }
    }
  );
}
